export default class UserSettingsInBrowserStorage {
	#storage = window.localStorage;
	static #PROPS = Object.freeze({
		"WL_BAND": {key: "PEAKSEL-DEFAULT-WL-BAND", default: 4},
		// Since we have a requirement which says: "When the number of plates+layers > 4, the default choice is Table"
		// we cannot define a default here because it makes it impossible to determine whether the user has
		// changed a default, and we should ignore the plate count, or is it just a default representation defined by us?
		// Having a null clearly says that the User did not change view mode, and we can apply some rules when deciding
		// which view would be better to show.
		"BATCH_VIEW_MODE": {key: "PEAKSEL-DEFAULT-BATCH-VIEW-MODE", default: null},
		"CARD_ORDER": {key: "PEAKSEL-CHROMATOGRAM-CARD-ORDER", default: null},
		"CARD_SIZES": {key: "PEAKSEL-INJECTION-CARD-SIZES", default: null},
		"ANALYTE_SOURCES": {key: "PEAKSEL-RECENT-ANALYTE-SOURCES", default: null}
	});
	static #MAX_ANALYTE_SOURCES = 5;
	static #ID_PATTERN = /^[\w-]{11,12}$/;

	/**
	 * @returns {number}
	 */
	getDefaultWlBand() {
		const prop = UserSettingsInBrowserStorage.#PROPS.WL_BAND;
		return +(this.#storage.getItem(prop.key) || prop.default);
	}

	/**
	 * @param {number || string} wlBand
	 */
	setDefaultWlBand(wlBand) {
		this.#storage.setItem(UserSettingsInBrowserStorage.#PROPS.WL_BAND.key, wlBand);
	}

	/**
	 * @return {string|null}
	 */
	getUserDefinedBatchViewMode() {
		const prop = UserSettingsInBrowserStorage.#PROPS.BATCH_VIEW_MODE;
		return this.#storage.getItem(prop.key) || prop.default;
	}

	/**
	 * @param {string} viewMode
	 */
	setUserDefinedBatchViewMode(viewMode) {
		this.#storage.setItem(UserSettingsInBrowserStorage.#PROPS.BATCH_VIEW_MODE.key, viewMode);
	}

	/**
	 * @return {string[]} card names in the user's preferred order, or [] if none was saved
	 */
	getChromatogramCardOrder() {
		const prop = UserSettingsInBrowserStorage.#PROPS.CARD_ORDER;
		const stored = this.#storage.getItem(prop.key);
		return stored ? [...new Set(JSON.parse(stored))] : (prop.default || []);
	}

	/**
	 * Saves the whole order the user is looking at so that a reload reproduces it exactly.
	 * One saved list is shared by every injection, so these cards are woven into it one by one instead
	 * of going in as a block: a card this injection doesn't have is re-inserted after everything in the
	 * saved order that it used to follow and this injection also has.
	 * @param {string[]} cardNames
	 */
	setChromatogramCardOrder(cardNames) {
		const merged = [...cardNames];
		let anchor = 0; // where the next missing card goes
		for (const name of this.getChromatogramCardOrder()) {
			const at = merged.indexOf(name);
			if (at < 0) // this injection doesn't have it, insert at the anchor position
				merged.splice(anchor++, 0, name);
			else
				anchor = Math.max(anchor, at + 1);
		}
		this.#storage.setItem(UserSettingsInBrowserStorage.#PROPS.CARD_ORDER.key, JSON.stringify(merged));
	}

	/**
	 * Heights are kept in % of the window height, so a card takes up the same share of the screen no
	 * matter which monitor the page is opened on.
	 * @return {Map<string, {height: ?number, expanded: boolean}>}
	 */
	getInjectionCardSizes() {
		const stored = this.#storage.getItem(UserSettingsInBrowserStorage.#PROPS.CARD_SIZES.key);
		if (!stored)
			return new Map();
		let parsed;
		try {
			parsed = JSON.parse(stored);
		} catch (e) {
			return new Map();
		}
		return new Map(Object.entries(parsed || {})
			.filter(([, size]) => size && typeof size === 'object')
			.map(([name, size]) => [name, {
				// Check garbage values, so UI wouldn't brake
				height: Number.isFinite(size.height) && size.height > 0 ? size.height : null,
				expanded: size.expanded === true
			}])
			.filter(([, size]) => size.height !== null || size.expanded));
	}

	/**
	 * @param {Map<string, ?{height: ?number, expanded: boolean}>} sizeByCardName - a null size forgets that
	 * card, which makes it fall back to the height the CSS gives it
	 */
	setInjectionCardSizes(sizeByCardName) {
		const merged = this.getInjectionCardSizes();
		for (const [name, size] of sizeByCardName)
			if (size === null)
				merged.delete(name);
			else
				merged.set(name, size);
		this.#storage.setItem(UserSettingsInBrowserStorage.#PROPS.CARD_SIZES.key,
			JSON.stringify(Object.fromEntries(merged)));
	}

	/**
	 * @param {string} orgId
	 * @return {{id: string, name: string}[]}
	 */
	getRecentAnalyteSources(orgId) {
		const injections = this.#allRecentAnalyteSources()[orgId];
		return Array.isArray(injections)
			? injections.filter(injection => injection && typeof injection.id === 'string'
					&& UserSettingsInBrowserStorage.#ID_PATTERN.test(injection.id))
				.slice(0, UserSettingsInBrowserStorage.#MAX_ANALYTE_SOURCES)
			: [];
	}

	/** @return {Object<string, {id: string, name: string}[]>} recent injections keyed by org id */
	#allRecentAnalyteSources() {
		let parsed;
		try {
			parsed = JSON.parse(this.#storage.getItem(UserSettingsInBrowserStorage.#PROPS.ANALYTE_SOURCES.key));
		} catch (e) {
			return {};
		}
		return parsed && typeof parsed === 'object' && !Array.isArray(parsed) ? parsed : {};
	}

	#saveRecentAnalyteSources(orgId, injections) {
		const all = this.#allRecentAnalyteSources();
		all[orgId] = injections;
		this.#storage.setItem(UserSettingsInBrowserStorage.#PROPS.ANALYTE_SOURCES.key, JSON.stringify(all));
	}

	/**
	 * @param {string} orgId
	 * @param {{id: string, name: string}} injection
	 */
	addRecentAnalyteSource(orgId, injection) {
		const kept = [{id: injection.id, name: injection.name},
			...this.getRecentAnalyteSources(orgId).filter(i => i.id !== injection.id)]
			.slice(0, UserSettingsInBrowserStorage.#MAX_ANALYTE_SOURCES);
		this.#saveRecentAnalyteSources(orgId, kept);
	}

	/**
	 * Forgets an injection the user can no longer import from (it's gone or their access to it is).
	 * @param {string} orgId
	 * @param {string} injectionId
	 */
	removeRecentAnalyteSource(orgId, injectionId) {
		const kept = this.getRecentAnalyteSources(orgId).filter(i => i.id !== injectionId);
		this.#saveRecentAnalyteSources(orgId, kept);
	}

	/**
	 * Re-sorts the given names the way a page reload would show them: saved ones first, in the saved
	 * order, then the ones nothing was ever saved for.
	 * @param {string[]} cardNames
	 * @return {string[]}
	 */
	sortByChromatogramCardOrder(cardNames) {
		const saved = this.getChromatogramCardOrder();
		return [
			...saved.filter(name => cardNames.includes(name)),
			...cardNames.filter(name => !saved.includes(name))
		];
	}
}